// src/components/product/EditProductModal.tsx
// Modal for editing existing products in the catalog
// Uses hierarchical category dropdowns like AddItemModal

import React, { useState, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import { productCatalogAPI, Product, UpdateProductRequest } from '../../services/productCatalogAPI';
import { CategoryHierarchy } from '../../types/category';
import { tenantConfigAPI } from '../../services/tenantConfigAPI';
interface EditProductModalProps {
  product: Product;
  onClose: () => void;
  onProductUpdated: (product: Product) => void;
  categories: CategoryHierarchy[];
}

const EditProductModal: React.FC<EditProductModalProps> = ({ product, onClose, onProductUpdated, categories }) => {
  const [formData, setFormData] = useState<UpdateProductRequest>({
    name: product.name,
    manufacturer: product.manufacturer,
    model: product.model,
    categoryId: product.categoryId,
    subcategoryId: product.subcategoryId,
    typicalUnitCost: product.typicalUnitCost,
    preferredVendor: product.preferredVendor,
    defaultLocation: product.defaultLocation,
    minStockLevel: product.minStockLevel,
    reorderPoint: product.reorderPoint,
    unitOfMeasure: product.unitOfMeasure,
    description: product.description,
    isActive: product.isActive,
    isDiscontinued: product.isDiscontinued
  });

  const [selectedParentCategory, setSelectedParentCategory] = useState<CategoryHierarchy | null>(null);
  const [availableSubcategories, setAvailableSubcategories] = useState<CategoryHierarchy[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Storage locations from tenant config
  const [storageLocations, setStorageLocations] = useState<string[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);

  // Load storage locations on mount
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const locations = await tenantConfigAPI.getInventoryLocations();
        setStorageLocations(locations);
      } catch (err) {
        console.error('Failed to load storage locations:', err);
      } finally {
        setLocationsLoading(false);
      }
    };
    loadLocations();
  }, []);

  // Build parent categories (top-level only)
  const parentCategories = categories.filter(c => !c.parentCategoryId);

  // Initialize parent category and subcategories on mount
  useEffect(() => {
    if (product.categoryId && categories.length > 0) {
      // Find the category
      const category = categories.find(c => c.categoryId === product.categoryId);
      
      if (category?.parentCategoryId) {
        // This is a subcategory
        const parent = categories.find(c => c.categoryId === category.parentCategoryId);
        if (parent) {
          setSelectedParentCategory(parent);
          const subcats = categories.filter(c => c.parentCategoryId === parent.categoryId);
          setAvailableSubcategories(subcats);
        }
      } else if (category) {
        // This is a parent category
        setSelectedParentCategory(category);
        const subcats = categories.filter(c => c.parentCategoryId === category.categoryId);
        setAvailableSubcategories(subcats);
      }
    }
  }, [product.categoryId, categories]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'number' ? (value ? parseFloat(value) : undefined) : value
      }));
    }
  };

  const handleParentCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const parentCategoryId = e.target.value;
    
    const parentCategory = categories.find(c => c.categoryId === parentCategoryId);
    setSelectedParentCategory(parentCategory || null);
    
    // Find subcategories
    const subcategories = categories.filter(c => c.parentCategoryId === parentCategoryId);
    setAvailableSubcategories(subcategories);
    
    // If no subcategories, use parent as the category
    if (subcategories.length === 0) {
      setFormData(prev => ({
        ...prev,
        categoryId: parentCategoryId,
        subcategoryId: undefined
      }));
    } else {
      // Clear category selection, force user to pick subcategory
      setFormData(prev => ({
        ...prev,
        categoryId: '',
        subcategoryId: undefined
      }));
    }
  };

  const handleSubcategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const subcategoryId = e.target.value;
    
    setFormData(prev => ({
      ...prev,
      categoryId: subcategoryId,
      subcategoryId: subcategoryId
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name?.trim()) {
      setError('Product name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Clean up empty strings to undefined
      const cleanedData: UpdateProductRequest = {
        ...formData,
        manufacturer: formData.manufacturer?.trim() || undefined,
        model: formData.model?.trim() || undefined,
        subcategoryId: formData.subcategoryId?.trim() || undefined,
        preferredVendor: formData.preferredVendor?.trim() || undefined,
        defaultLocation: formData.defaultLocation?.trim() || undefined,
        unitOfMeasure: formData.unitOfMeasure?.trim() || undefined,
        description: formData.description?.trim() || undefined
      };

      const updatedProduct = await productCatalogAPI.updateProduct(product.productId, cleanedData);
      
      console.log('✅ Product updated successfully:', updatedProduct.productId);
      onProductUpdated(updatedProduct);
      
    } catch (err: any) {
      console.error('Error updating product:', err);
      setError(err.message || 'Failed to update product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getSubcategoryState = () => {
    if (!selectedParentCategory) {
      return {
        disabled: true,
        placeholder: 'Select main category first',
        options: [],
        className: 'flex-1 px-3 py-2 border rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed border-gray-300'
      };
    }
    
    if (availableSubcategories.length === 0) {
      return {
        disabled: true,
        placeholder: 'No subcategories available',
        options: [],
        className: 'flex-1 px-3 py-2 border rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed border-gray-300'
      };
    }
    
    return {
      disabled: false,
      placeholder: 'Select subcategory...',
      options: availableSubcategories,
      className: 'flex-1 px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500 border-gray-300'
    };
  };

  const subcategoryState = getSubcategoryState();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Edit Product</h2>
            <p className="text-sm text-gray-500 mt-1">
              {product.trackingType || product.productType} • ID: {product.productId}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
            disabled={loading}
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Product Information Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Product Information</h3>
            
            <div className="space-y-4">
              {/* Product Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Manufacturer and Model */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Manufacturer
                  </label>
                  <input
                    type="text"
                    name="manufacturer"
                    value={formData.manufacturer || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Model
                  </label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Category Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                
                <div className="space-y-2">
                  {/* Parent Category */}
                  <select 
                    value={selectedParentCategory?.categoryId || ''} 
                    onChange={handleParentCategoryChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select main category...</option>
                    {parentCategories.map(category => (
                      <option key={category.categoryId} value={category.categoryId}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  
                  {/* Subcategory */}
                  <div className="flex items-center space-x-2">
                    <ChevronRight className={`w-4 h-4 ${subcategoryState.disabled ? 'text-gray-300' : 'text-gray-400'}`} />
                    <select 
                      value={formData.subcategoryId || ''}
                      onChange={handleSubcategoryChange}
                      disabled={subcategoryState.disabled}
                      className={subcategoryState.className}
                    >
                      <option value="">{subcategoryState.placeholder}</option>
                      {subcategoryState.options.map(subcat => (
                        <option key={subcat.categoryId} value={subcat.categoryId}>
                          {subcat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Selection Display */}
                  {selectedParentCategory && (
                    <div className="text-xs text-gray-500 flex items-center space-x-1">
                      <span>Selected:</span>
                      <span className="font-medium">{selectedParentCategory.name}</span>
                      {formData.subcategoryId && (
                        <>
                          <ChevronRight className="w-3 h-3" />
                          <span className="font-medium">
                            {availableSubcategories.find(s => s.categoryId === formData.subcategoryId)?.name}
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Procurement Defaults Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Procurement Defaults</h3>
            
            <div className="space-y-4">
              {/* Typical Unit Cost and Preferred Vendor */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Typical Unit Cost
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">$</span>
                    <input
                      type="number"
                      name="typicalUnitCost"
                      value={formData.typicalUnitCost || ''}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preferred Vendor
                  </label>
                  <input
                    type="text"
                    name="preferredVendor"
                    value={formData.preferredVendor || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

             {/* Default Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Location
                </label>
                <select
                  name="defaultLocation"
                  value={formData.defaultLocation || ''}
                  onChange={handleInputChange}
                  disabled={locationsLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Select storage location...</option>
                  {storageLocations.map((loc) => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
                {locationsLoading && (
                  <p className="text-xs text-gray-500 mt-1">Loading locations...</p>
                )}
                {!locationsLoading && storageLocations.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    No storage locations configured. Add them in Admin → Tenant Settings.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Inventory Rules Section (for BULK items) */}
          {(product.trackingType || product.productType) !== 'SERIALIZED' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Inventory Rules (Bulk Items)</h3>
              
              <div className="space-y-4">
                {/* Min Stock Level, Reorder Point, Unit of Measure */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Min Stock Level
                    </label>
                    <input
                      type="number"
                      name="minStockLevel"
                      value={formData.minStockLevel || ''}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reorder Point
                    </label>
                    <input
                      type="number"
                      name="reorderPoint"
                      value={formData.reorderPoint || ''}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unit of Measure
                    </label>
                    <input
                      type="text"
                      name="unitOfMeasure"
                      value={formData.unitOfMeasure || ''}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description || ''}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Product Status */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Product Status</h3>
            
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Active (visible in purchase dropdowns)
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="isDiscontinued"
                  checked={formData.isDiscontinued}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Discontinued (no longer available from vendor)
                </span>
              </label>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Updating...
                </>
              ) : (
                'Update Product'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProductModal;
